import { ExerciseRow, ExportedExercise } from './types';
import { RemovalPreviewExercise } from './types';

export function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

export function parseCSV(text: string): ExerciseRow[] {
  const lines = text.split(/\r?\n|\r/).filter((line) => line.trim());
  if (lines.length === 0) return [];

  const headers = parseCSVLine(lines[0] || '').map((h) => h.toLowerCase());
  const exercises: ExerciseRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i] || '');
    const exercise: ExerciseRow = {
      rowNumber: i,
      name: '',
      type: '',
      body_parts: '',
      equipment: '',
      default_metrics: '',
      video_url: '',
      image_url: '',
      tags: '',
    };

    headers.forEach((header, index) => {
      const value = values[index];
      if (value !== undefined && value !== '') {
        exercise[header as keyof Omit<ExerciseRow, 'rowNumber'>] = value;
      }
    });

    if (exercise.name && exercise.type) {
      exercises.push(exercise as ExerciseRow);
    }
  }

  return exercises;
}

export function exercisesToCSV(exercises: ExportedExercise[]): string {
  const headers = [
    'name',
    'type',
    'body_parts',
    'equipment',
    'default_metrics',
    'video_url',
    'image_url',
    'tags',
  ];

  const csvRows = [headers.join(',')];

  exercises.forEach((exercise) => {
    const row = [
      `"${exercise.name.replace(/"/g, '""')}"`,
      `"${exercise.type.replace(/"/g, '""')}"`,
      `"${exercise.body_parts.replace(/"/g, '""')}"`,
      `"${exercise.equipment.replace(/"/g, '""')}"`,
      `"${exercise.default_metrics.replace(/"/g, '""')}"`,
      `"${exercise.video_url.replace(/"/g, '""')}"`,
      `"${exercise.image_url.replace(/"/g, '""')}"`,
      `"${exercise.tags.replace(/"/g, '""')}"`,
    ];
    csvRows.push(row.join(','));
  });

  return csvRows.join('\n');
}

export const CUI_COVER_PAGE_FILENAME = 'gov-cover-page.pdf';

/** PDF files begin with the `%PDF-` magic bytes. */
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46] as const; // %PDF

/**
 * True when `data` starts with the `%PDF` magic bytes. Used to reject a
 * successful (HTTP 200) response that isn't actually the CUI cover PDF — e.g.
 * a misrouted HTML page or error body — before its bytes get zipped as
 * `gov-cover-page.pdf`.
 */
export function isPdf(data: ArrayBuffer | Uint8Array): boolean {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  return PDF_MAGIC.every((byte, index) => bytes[index] === byte);
}

/**
 * Builds the zip archive downloaded for CUI exercise-library exports: the
 * CUI-prefixed CSV plus the same cover-page PDF the print worker prepends to
 * government printouts. A CSV can't carry a CUI banner, so the designation
 * rides alongside it in the zip.
 */
export async function buildCuiExportZip(
  csvContent: string,
  csvFilename: string,
  coverPage: ArrayBuffer | Uint8Array,
): Promise<Blob> {
  // Lazy import keeps jszip out of the main bundle — only gov exports pay
  // for it.
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  zip.file(csvFilename, csvContent);
  zip.file(CUI_COVER_PAGE_FILENAME, coverPage);
  const zipData = await zip.generateAsync({ type: 'arraybuffer' });
  return new Blob([zipData], { type: 'application/zip' });
}

/**
 * Downloads the CUI exercise-library export as a single zip containing the
 * cover page and the CSV.
 */
export async function downloadCuiExportZip(
  csvContent: string,
  csvFilename: string,
): Promise<void> {
  const coverPage = await fetch(
    `${import.meta.env.BASE_URL}${CUI_COVER_PAGE_FILENAME}`,
  ).then(async (res) => {
    if (!res.ok) {
      throw new Error(`Failed to load ${CUI_COVER_PAGE_FILENAME}`);
    }
    const buffer = await res.arrayBuffer();
    // Reject a 200 that isn't actually the cover PDF (e.g. a misrouted HTML
    // page) so we never stamp non-PDF bytes as the CUI designation.
    if (!isPdf(buffer)) {
      throw new Error(
        `${CUI_COVER_PAGE_FILENAME} is not a valid PDF (received ${
          res.headers.get('content-type') ?? 'unknown content-type'
        })`,
      );
    }
    return buffer;
  });
  const blob = await buildCuiExportZip(csvContent, csvFilename, coverPage);
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${csvFilename.replace(/\.csv$/, '')}.zip`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Case-insensitive substring filter over the hand-pick removal candidate
 * list. An empty/whitespace-only query returns the full list unfiltered.
 */
export function filterExercisesByName(
  exercises: RemovalPreviewExercise[],
  query: string,
): RemovalPreviewExercise[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return exercises;
  return exercises.filter((exercise) =>
    exercise.name.toLowerCase().includes(trimmed),
  );
}

export type DefaultExerciseFilter = 'all' | 'default' | 'non_default';

/** Filter the existing-exercises list by platform-default (`isDefault`) flag. */
export function filterExercisesByDefault(
  exercises: RemovalPreviewExercise[],
  filter: DefaultExerciseFilter,
): RemovalPreviewExercise[] {
  if (filter === 'all') return exercises;
  if (filter === 'default') {
    return exercises.filter((exercise) => exercise.isDefault === true);
  }
  return exercises.filter((exercise) => exercise.isDefault !== true);
}

/**
 * Filter the existing-exercises list by scope (base library vs. team).
 *
 * - `selectedTeamId` set: only base-library rows (if `showBaseLibrary`) and
 *   rows belonging to that team are visible — other teams' exercises drop out.
 * - `selectedTeamId` null: every team's exercises stay visible; the toggle
 *   only controls whether base-library rows are included.
 *
 * `showBaseLibrary` applies independently of `selectedTeamId` — toggling it
 * off hides base-library rows whether or not a team is selected.
 */
export function filterExercisesByScope(
  exercises: RemovalPreviewExercise[],
  selectedTeamId: string | null,
  showBaseLibrary: boolean,
): RemovalPreviewExercise[] {
  return exercises.filter((exercise) => {
    const isBaseLibrary = exercise.teamId == null;
    if (isBaseLibrary) return showBaseLibrary;
    if (selectedTeamId) return exercise.teamId === selectedTeamId;
    return true;
  });
}

export function toExerciseInput(exercise: ExerciseRow) {
  return {
    name: exercise.name,
    type: exercise.type,
    body_parts: exercise.body_parts,
    equipment: exercise.equipment,
    default_metrics: exercise.default_metrics,
    video_url: exercise.video_url,
    image_url: exercise.image_url,
    tags: exercise.tags,
  };
}
